// EstimateEditor
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  getEstimate,
  setLocalEstimate,
  subscribeEstimateSockets,
  updateEstimateSocket,
  finalizeEstimate,
} from '../../actions/estimateActions';

import axios from '../../helpers/axios';
import RoomsTable from './RoomsTable';
import MaterialsTable from './MaterialsTable';
import WorksTable from './WorksTable';
import TotalsBar from './TotalsBar';

import { ROOM_CATALOG, FLAT_PRESETS, MATERIAL_CATALOG, WORK_CATALOG } from './catalogs';
import { measures as mkMeasures, scopeBase, ceilPacks, clamp } from './math';
import { MainContainer } from '../MainContainer';

import EditorActions from './EditorActions';
import MetaCard from './MetaCard';
import RoomsSection from './RoomsSection';
import MaterialsSection from './MaterialsSection';
import WorksSection from './WorksSection';
import RoomPresence from '../Presence/RoomPresence';
import CallOverlay from '../../components/call/CallOverlay';

const LS = (id) => `est:${id}`;

const DEFAULT_META = {
  company: 'ALOTB', site: 'https://alotb.ru/', markup: 10, discount: 0, hideMarkup: false,
  client: '', docnum: '', date: new Date().toISOString().slice(0, 10),
  fioContractor: '', fioCustomer: '', withAgreement: true,
  note: 'Расчёт ориентировочный. Финальные объёмы уточняются после замеров.',
  agreementText: `1) Подрядчик обязуется выполнить работы, указанные в разделе «Работы по проекту» с соблюдением норм и сроков.
2) Заказчик обязуется обеспечить доступ на объект и произвести оплату в срок.
3) Стоимость определяется настоящим документом и может корректироваться доп. соглашениями.`,
};

const STATIC_BASE = `${process.env.PUBLIC_URL || ''}/ALOTB_calc_v30`;

async function fetchText(rel) {
  const res = await fetch(`${STATIC_BASE}/${rel}`, { cache: 'no-store' });
  if (!res.ok) throw new Error(`HTTP ${res.status} for ${rel}`);
  return await res.text();
}

function extractBody(html) {
  const m = /<body[^>]*>([\s\S]*?)<\/body>/i.exec(html);
  return m ? m[1] : html;
}

const agreementTemplate = (meta = {}, company = "") => {
  const customer = (meta.client || "Заказчик").trim();
  const contractor = (meta.fioContractor || company || "Исполнитель").trim();
  return `${customer}, именуемый в дальнейшем «Заказчик», с одной стороны, и ${contractor}, именуемый в дальнейшем «Исполнитель», с другой стороны, именуемые вместе «Стороны», а по отдельности — «Сторона», заключили настоящий договор (далее — «Договор») о нижеследующем.`;
};

export default function EstimateEditor() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const { data } = useSelector((s) => s.estimate.current);
  const userId = useSelector((s) => s.auth?.user?._id);

  const [author] = useState(() => 'u_' + Math.random().toString(36).slice(2));
  const [pdfBusy, setPdfBusy] = useState(false);

  const rawBase = (axios?.defaults?.baseURL || '').replace(/\/+$/, '');
  const apiOrigin = rawBase.replace(/\/api(?:\/.*)?$/, '');
  const makeApiUrl = (rel) => (rel?.startsWith('http') ? rel : `${apiOrigin}${rel || ''}`);

  const deepClone = (x) => {
    try { if (typeof structuredClone === "function") return structuredClone(x); } catch { }
    try { return JSON.parse(JSON.stringify(x)); } catch { return x; }
  };

  const mkMeasuresSafe = (x) => mkMeasures(deepClone(x || {}));

  const setParams = (params) => patch({ params });

  useEffect(() => {
    if (id) dispatch(getEstimate(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (!id) return;
    const unsub = dispatch(subscribeEstimateSockets(id, author));
    return () => unsub && unsub();
  }, [id, author, dispatch]);

  useEffect(() => {
    const key = LS(id);
    if (!data || (!data.rooms?.length && !data.materials?.length && !data.works?.length)) {
      try {
        const saved = JSON.parse(localStorage.getItem(key) || 'null');
        if (saved) {
          dispatch(setLocalEstimate(saved));
          dispatch(updateEstimateSocket(id, saved, author));
        }
      } catch { }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const key = LS(id);
    if (!data) return;
    const t = setTimeout(() => localStorage.setItem(key, JSON.stringify(data)), 120);
    return () => clearTimeout(t);
  }, [data, id]);

  const S = useMemo(
    () => mkMeasuresSafe(data || {}),
    [data?.rooms, data?.bathroom, data?.params, data?.meta?.markup, data?.meta?.discount, data?.meta?.hideMarkup]
  );

  const recalcTotals = (draft) => {
    const S2 = mkMeasuresSafe(draft || {});

    let matTotal = 0;
    (draft.materials || []).forEach((m) => {
      const baseVolAuto = scopeBase(m, S2, draft);
      const baseVol = m._manualVol ? Number(m._manualVolVal || 0) : baseVolAuto;
      const norm = (m._normManual != null) ? Number(m._normManual || 0) : Number(m.norm || 0);

      const needUnits = baseVol * norm;
      const withExtra = needUnits * (1 + Number(m.extraPct || 0) / 100);

      const packsAuto = ceilPacks(withExtra, m.packSize || 1);
      const packs = m._manualPacks ? Number(m._manualPacksVal || 0) : packsAuto;

      const sum = Math.round((packs * (m.price || 0)) || 0);

      m.baseVol = Number.isFinite(baseVol) ? +baseVol.toFixed(2) : 0;
      m._packsAuto = Number.isFinite(packsAuto) ? packsAuto : 0;
      m.sum = sum;
      matTotal += sum;
    });

    let workBase = 0;
    (draft.works || []).forEach((w) => {
      const qty = w._manualQty ? clamp(w._manualQtyVal) : clamp(w.autoQty);
      const base = (qty * (w.price || 0)) || 0;
      w._add = 0;
      w.sum = Math.round(base);
      workBase += base;
    });

    const markupPct = clamp(draft.meta?.markup || 0);
    let mark = Math.max(0, Math.round((workBase + matTotal) * (markupPct / 100)));

    if (draft.meta?.hideMarkup && workBase > 0 && mark > 0) {
      let distributed = 0;
      draft.works.forEach((w, idx) => {
        const qty = w._manualQty ? clamp(w._manualQtyVal) : clamp(w.autoQty);
        const base = (qty * (w.price || 0)) || 0;
        const add = idx === draft.works.length - 1 ? (mark - distributed) : Math.round(mark * (base / workBase));
        w._add = add;
        w.sum = Math.round(base + add);
        distributed += add;
      });
    }

    const discount = clamp(draft.meta?.discount || 0);
    const subtotal = (draft.meta?.hideMarkup ? workBase : (workBase + mark)) + matTotal;
    const all = Math.round(subtotal * (1 - discount / 100));

    draft.totals = { mat: Math.round(matTotal), work: Math.round(workBase), mark, all };
  };

  const enrichAutoFields = (draft) => {
    const S2 = mkMeasuresSafe(draft || {});
    draft.works = (draft.works || []).map((w) => {
      if (!w._manualQty) {
        const cat = WORK_CATALOG.find((x) => x.key === w.key);
        const qty = cat?.qtyFn ? cat.qtyFn(S2, draft) : w.autoQty;
        return { ...w, autoQty: clamp(qty) };
      }
      return w;
    });

    draft.materials = (draft.materials || []).map((m) => {
      if (m._normManual == null) {
        const cat = MATERIAL_CATALOG.find((x) => x.key === m.key);
        if (cat?.normFn) {
          const base = scopeBase(m, S2, draft);
          const v = cat.normFn(
            cat.scope === 'plinth' ? (draft.params?.plinth_m || 0) : base,
            { screed_mm: draft.params?.screed_mm || 60 }
          );
          return { ...m, norm: (base > 0 ? clamp(v / base) : clamp(m.norm || 0)) || clamp(m.norm || 0) };
        }
      }
      return m;
    });
  };

  const pendingRef = useRef(null);
  const timerRef = useRef(null);

  const flushSocket = useCallback(() => {
    if (!pendingRef.current) return;
    const payload = deepClone(pendingRef.current);
    pendingRef.current = null;
    dispatch(updateEstimateSocket(id, payload, author));
  }, [dispatch, id, author]);

  const scheduleSocket = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flushSocket, 160);
  }, [flushSocket]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const patch = (p) => {
    const next = { ...data, ...p };

    const calcDraft = deepClone(next);
    enrichAutoFields(calcDraft);
    recalcTotals(calcDraft);

    next.materials = calcDraft.materials;
    next.works = calcDraft.works;
    next.totals = calcDraft.totals;

    next._client = author;
    next._rev = (data?._rev || 0) + 1;
    next._ts = Date.now();

    dispatch(setLocalEstimate(next));
    pendingRef.current = deepClone(next);
    scheduleSocket();
  };

  const setRooms = (rooms) => patch({ rooms });
  const delRoom = (i) => setRooms((data.rooms || []).filter((_, idx) => idx !== i));
  const clearRooms = () => setRooms([]);

  const addRoomTop = (preset) => {
    const r = preset ? { ...preset } : { name: 'Помещение', len: '', wid: '', area: 1, floor: 'lam', warm: false, wall: 4 };
    setRooms([r, ...(data.rooms || [])]);
  };

  const addRoomsPreset = (kind) => {
    const map = new Map(ROOM_CATALOG.map((r) => [r.key, r]));
    const keys = FLAT_PRESETS[kind] || [];
    const arr = keys.map((k) => ({ ...map.get(k) }));
    setRooms(arr);
  };

  const setMaterials = (materials) => patch({ materials });
  const delMat = (i) => setMaterials((data.materials || []).filter((_, idx) => idx !== i));
  const clearMat = () => setMaterials([]);

  const addMaterialTop = (catKey) => {
    if (!catKey) {
      setMaterials([
        { key: 'custom' + Date.now(), name: 'Материал', norm: 1, _normManual: 1, packSize: 1, packName: 'упаковка', price: 0, scope: 'floorAll' },
        ...(data.materials || []),
      ]);
      return;
    }
    const base = MATERIAL_CATALOG.find((x) => x.key === catKey);
    if (!base) return;

    const obj = {
      key: base.key, name: base.name, scope: base.scope,
      packSize: base.packSize, packName: base.packName, price: base.price, note: base.note,
      _manualVol: false, _manualVolVal: null, _manualPacks: false, _manualPacksVal: null,
    };

    const draft = { ...data, materials: [...(data.materials || [])] };
    const S0 = mkMeasuresSafe(draft);
    const baseVol = scopeBase(base, S0, draft);

    if (base.normFn) {
      const val = base.normFn(base.scope === 'plinth' ? (draft.params?.plinth_m || 0) : baseVol, { screed_mm: draft.params?.screed_mm || 60 });
      obj.norm = baseVol > 0 ? clamp(val / baseVol) : clamp(val || 0);
    } else {
      obj.norm = 1;
    }

    setMaterials([obj, ...(data.materials || [])]);
  };

  const setWorks = (works) => patch({ works });
  const delWork = (i) => setWorks((data.works || []).filter((_, idx) => idx !== i));
  const clearWorks = () => setWorks([]);

  const addWorkTop = (catKey) => {
    if (!catKey) {
      setWorks([
        { key: 'custom' + Date.now(), name: 'Новая работа', unit: 'м²', price: 0, desc: '', _manualQty: true, _manualQtyVal: 1 },
        ...(data.works || []),
      ]);
      return;
    }
    const base = WORK_CATALOG.find((x) => x.key === catKey);
    if (!base) return;
    const qty = base.qtyFn ? base.qtyFn(S, data) : 0;
    setWorks([
      { key: base.key, name: base.name, unit: base.unit, price: base.price, desc: base.desc, _manualQty: false, _manualQtyVal: null, autoQty: clamp(qty) },
      ...(data.works || []),
    ]);
  };

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'alotb_smeta.json';
    a.click();
  };

  const importJSON = () => {
    const inp = document.createElement('input');
    inp.type = 'file';
    inp.accept = '.json,application/json';
    inp.onchange = async () => {
      const f = inp.files[0];
      if (!f) return;
      const txt = await f.text();
      try { const obj = JSON.parse(txt); patch(obj); } catch { alert('Ошибка JSON'); }
    };
    inp.click();
  };

  const saveHTML = async () => {
    try {
      const [css, htmlTpl, js] = await Promise.all([fetchText('app.css'), fetchText('index.html'), fetchText('app.js')]);
      const body = extractBody(htmlTpl);
      const safeState = JSON.stringify(data).replace(/</g, '\\u003C');
      const out = `<!DOCTYPE html>
<html lang="ru">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width, initial-scale=1"/>
  <title>ALOTB — Про-калькулятор ремонта (v3.0)</title>
  <style>${css}</style>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.10/pdfmake.min.js"></script>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.10/vfs_fonts.js"></script>
</head>
<body>
${body}
<script id="__SAVED_STATE__" type="application/json">${safeState}</script>
<script>${js}</script>
</body>
</html>`;
      const blob = new Blob([out], { type: 'text/html;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'ALOTB_Pro_Calc_v3.html';
      a.click();
    } catch (e) {
      alert('Не удалось собрать HTML: ' + e.message);
    }
  };

  const openLastPdf = () => {
    const last = Array.isArray(data?.pdfs) && data.pdfs.length ? data.pdfs[data.pdfs.length - 1] : null;
    const url = last ? makeApiUrl(last.url) : null;
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const makePdfServer = async () => {
    if (!id) return alert('Нет id документа');
    try {
      setPdfBusy(true);

      if (timerRef.current) clearTimeout(timerRef.current);

      const payload = deepClone(pendingRef.current || data);
      pendingRef.current = null;

      const ack = await dispatch(updateEstimateSocket(id, payload, author));
      if (!ack?.ok) {
        console.log('[makePdfServer] socket update not ok', ack);
      }

      const res = await dispatch(finalizeEstimate(id, userId));
      if (res?.url) {
        await dispatch(getEstimate(id));
        const url = makeApiUrl(res.url);
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        alert('Не удалось сформировать PDF');
      }
    } finally {
      setPdfBusy(false);
    }
  };

  const onClearAll = () => {
    if (window.confirm('Очистить всё и сбросить черновик?')) {
      patch({
        meta: { ...DEFAULT_META },
        params: { screed_mm: 60, plinth_m: 42, doors: 5, elec_override: null },
        bathroom: { floorArea: 4, wetWallArea: 18, perim: 8 },
        rooms: [], materials: [], works: [], totals: { mat: 0, work: 0, mark: 0, all: 0 },
      });
      localStorage.removeItem(LS(id));
    }
  };

  const meta = data?.meta || DEFAULT_META;
  const hasPdf = Array.isArray(data?.pdfs) && data.pdfs.length > 0;

  const roomId = id ? `estimate:${id}` : null;
  const presenceRooms = useSelector(s => s.presence?.rooms || {});
  const getPeerName = useCallback((peerId) => {
    const entry = presenceRooms[roomId] || {};
    const u = (entry.users || []).find(x => String(x.id) === String(peerId));
    return u?.name || '';
  }, [presenceRooms, roomId]);

  return (
    <MainContainer
      type="minimalFunctional"
      backButtonProduct
      href="/admin/estimates/"
      title="Редактор сметы"
    >
      <div className="est-pro">
        {roomId && <RoomPresence roomId={roomId} />}
        <CallOverlay getPeerName={getPeerName} />
        <div className="wrap">
          <MetaCard
            meta={meta}
            data={data}
            onPatch={patch}
            agreementTemplate={agreementTemplate}
          />

          <div className="card" style={{ marginTop: 12, paddingTop: 12 }}>
            <div className="section-title"><i></i>Отображение разделов в редакторе и PDF</div>
            <div className="row" style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
              <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={meta.showRooms !== false}
                  onChange={(e) => patch({ meta: { ...meta, showRooms: e.target.checked } })}
                />
                Показывать помещения
              </label>

              <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={meta.showMaterials !== false}
                  onChange={(e) => patch({ meta: { ...meta, showMaterials: e.target.checked } })}
                />
                Показывать материалы
              </label>

              <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={meta.showWorks !== false}
                  onChange={(e) => patch({ meta: { ...meta, showWorks: e.target.checked } })}
                />
                Показывать работы
              </label>
            </div>
          </div>

          {meta.showRooms !== false && (
            <RoomsSection
              data={data}
              addRoomsPreset={addRoomsPreset}
              addRoomTop={addRoomTop}
              clearRooms={clearRooms}
              setRooms={setRooms}
              delRoom={delRoom}
              RoomsTable={RoomsTable}
              setParams={setParams}
            />
          )}

          {meta.showMaterials !== false && (
            <MaterialsSection
              data={data}
              addMaterialTop={addMaterialTop}
              clearMat={clearMat}
              setMaterials={setMaterials}
              delMat={delMat}
              MaterialsTable={MaterialsTable}
            />
          )}

          {meta.showWorks !== false && (
            <WorksSection
              data={data}
              addWorkTop={addWorkTop}
              clearWorks={clearWorks}
              setWorks={setWorks}
              delWork={delWork}
              WorksTable={WorksTable}
            />
          )}

          <TotalsBar
            totals={data.totals}
            meta={data.meta}
            onChangeNote={(val) => patch({ meta: { ...meta, note: val } })}
          />

          <EditorActions
            pdfBusy={pdfBusy}
            hasPdf={hasPdf}
            onMakePdf={makePdfServer}
            onOpenLastPdf={openLastPdf}
            onSaveHTML={saveHTML}
            onExportJSON={exportJSON}
            onImportJSON={importJSON}
            onClearAll={onClearAll}
          />
        </div>
      </div>
    </MainContainer>
  );
}
